import { ErrorMessage } from '@/components/ui/ErrorMessage'
import { TFunction } from 'i18next'

export type ValidationType = Record<
  string,
  { condition: boolean; message: string }[]
>

export interface FormInterface<T> {
  validateField(field: string): {
    error: boolean
    message: React.ReactNode | null
  }
  isFormValid(): boolean
  formDataForRequestBody(): T
}

export abstract class Form<T> implements FormInterface<T> {
  protected formData: T
  protected validations: ValidationType
  protected serverValidations: ValidationType
  private submitted: boolean
  protected t?: TFunction
  constructor(
    formData: T,
    validations: ValidationType,
    serverValidations: ValidationType,
    submitted: boolean,
    t?: TFunction
  ) {
    this.formData = formData
    this.validations = validations
    this.serverValidations = serverValidations
    this.submitted = submitted
    this.t = t
  }

  protected checkValidations(
    field: string,
    validations: ValidationType
  ): { error: boolean; message: React.ReactNode | null } | null {
    const fieldValidations = validations[field]
    if (fieldValidations) {
      for (const rule of fieldValidations) {
        if (rule?.condition) {
          const translatedMsg = this.t ? this.t(rule.message) : rule.message
          return {
            error: true,
            message: ErrorMessage(translatedMsg),
          }
        }
      }
    }
    return null
  }

  validateField(field: string): {
    error: boolean
    message: React.ReactNode | null
  } {
    if (!this.submitted) {
      return { error: false, message: null }
    }

    return (
      this.checkValidations(field, this.validations) ||
      this.checkValidations(field, this.serverValidations) || {
        error: false,
        message: null,
      }
    )
  }

  isFormValid(): boolean {
    return !Object.values(this.validations).some((rules) =>
      rules.some((rule) => rule.condition)
    )
  }

  abstract formDataForRequestBody(): any
}
